import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  type SelectChangeEvent,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import { useTranslation } from 'react-i18next';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { type GridColDef, type GridSortModel } from '@mui/x-data-grid';
import CustomStyledDataGrid from 'components/MuiDataGrid/CustomStyledDataGrid';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { useAppSelector } from 'store/hook';
import { selectAssociatedAssets } from 'store/asset.slice';
import DispatchMonitorAPI from 'api/dispatch-monitor.api';
import StatusCodesAPI from 'api/status-codes.api';
import { type StatusCodeRecord } from 'views/Settings/StatusCodes/types';
import LocationImporterAPI, {
  type ImportedLocation,
} from 'api/location-importer.api';
import {
  LocationZoneMapModal,
  type ZoneMapLocation,
} from 'components/Map/LocationZoneMapModal';
import { SseLogsViewer, type SseLogEntry } from 'components/Logs/SseLogsViewer';
import {
  isInternalRole,
  isInternalUser,
} from 'utils/validators/role.validation';

const MAX_SSE_LOGS = 300;

interface NearestLocation {
  locationId: string;
  name: string;
  easting: number;
  northing: number;
  distanceMeters: number;
  meta: Record<string, unknown> | null;
}

interface MakeModelDetails {
  make: string;
  model: string;
}

interface AssetInfo {
  bumperNumber: string;
  symbotId: string;
  assetType: string;
  makeModelDetails: MakeModelDetails | null;
  lat: number;
  lon: number;
  // location-type-agnostic nearest point — whichever location (of any type:
  // loading, dumping, stockpile, crusher, or otherwise) is physically
  // closest to the asset right now
  nearestLocation: NearestLocation | null;
}

interface LastStatus {
  payload: { status: string; reason: string; description?: string } | null;
  operatorName: string | null;
  operatorLoggedOut?: boolean;
}

// same reasoning as Haul Monitor's STATUS_COLORS — a few identity-defining
// status colors live outside the theme token system on purpose
const STATUS_COLORS: Record<string, string> = {
  active: '#4caf50',
  ready: '#4caf50',
  standby: '#3d5a99',
  delay: '#8b6914',
  down: '#8b2e2e',
};

const AssetsStatusInner: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const storeAssets = useAppSelector(selectAssociatedAssets);
  const accessToken = useAppSelector((s) => s.authReducer.accessToken);
  const customerId = useAppSelector((s) => s.persistedReducer.customer.id);
  const customerCode = useAppSelector((s) => s.persistedReducer.customer.code);
  const role = useAppSelector((s) => s.authReducer.role);
  const canViewSseLogs =
    isInternalRole(role ?? 'internal-user') || isInternalUser(role);

  const dispatchApi = useRef(new DispatchMonitorAPI()).current;
  const statusCodesApi = useRef(new StatusCodesAPI()).current;
  const locationApi = useRef(new LocationImporterAPI()).current;

  const [assets, setAssets] = useState<AssetInfo[]>([]);
  const [lastStatuses, setLastStatuses] = useState<Record<string, LastStatus>>(
    {}
  );
  const [statusCodes, setStatusCodes] = useState<StatusCodeRecord[]>([]);
  const [allLocations, setAllLocations] = useState<ImportedLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [search, setSearch] = useState('');
  const [equipmentTypeFilter, setEquipmentTypeFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);

  const [statusModal, setStatusModal] = useState<{
    asset: AssetInfo;
    status: string;
    reason: string;
    description: string;
  } | null>(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [mapLocation, setMapLocation] = useState<ZoneMapLocation | null>(null);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'bumperNumber', sort: 'asc' },
  ]);

  // dev/internal-only raw SSE log viewer — same buffering approach as Haul
  // Monitor: messages can arrive faster than React can usefully re-render,
  // so they're buffered here and flushed to state a few times a second
  // rather than once per message
  const [sseLogs, setSseLogs] = useState<SseLogEntry[]>([]);
  const sseLogSeqRef = useRef(0);
  const pendingSseLogsRef = useRef<SseLogEntry[]>([]);
  const sseLogFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const deviceLabelsBySymbotId = useMemo(() => {
    const map = new Map<string, string>();
    assets.forEach((a) => {
      map.set(a.symbotId, a.bumperNumber);
    });
    return map;
  }, [assets]);

  const getAssetId = (symbotId: string) =>
    storeAssets.find((a) => a.device?.deviceId === symbotId)?.assetId ?? null;

  const locationsById = useMemo(() => {
    const map = new Map<string, ImportedLocation>();
    allLocations.forEach((loc) => {
      map.set(loc.locationId, loc);
    });
    return map;
  }, [allLocations]);

  const fetchAll = useCallback(
    async (isRefresh: boolean) => {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const [assetRes, statusRes, scRes, locRes] = await Promise.allSettled([
        dispatchApi.getAssetInfo(),
        dispatchApi.getAssetLastStatus(),
        statusCodesApi.getAll(),
        locationApi.getLocations(),
      ]);

      const assetJson =
        assetRes.status === 'fulfilled' ? assetRes.value : { assets: [] };
      const statusJson =
        statusRes.status === 'fulfilled' ? statusRes.value : { assets: [] };
      const scJson = scRes.status === 'fulfilled' ? scRes.value : [];
      const locJson = locRes.status === 'fulfilled' ? locRes.value : [];

      const statusMap: Record<string, LastStatus> = {};
      (statusJson.assets ?? []).forEach((a: any) => {
        statusMap[a.symbotId] = {
          payload: a.status?.payload ?? null,
          operatorName: a.status?.operatorName ?? null,
        };
      });

      setAssets(assetJson.assets ?? []);
      setLastStatuses(statusMap);
      setStatusCodes(scJson);
      setAllLocations(locJson);
      setLoading(false);
      setRefreshing(false);
    },
    [dispatchApi, statusCodesApi, locationApi]
  );

  useEffect(() => {
    void fetchAll(false);
  }, [fetchAll]);

  const handleRefresh = () => {
    void fetchAll(true);
  };

  // live operator login/logout + status/reason updates — same SSE channel
  // and message shapes as Haul Monitor / Dispatch Plan V2
  // (xtab:{customerCode}:last:pubsub) — plus, for internal roles, the same
  // raw-message log capture Haul Monitor offers via its Logs viewer.
  useEffect(() => {
    if (!accessToken || !customerId || !customerCode) return undefined;

    const url = dispatchApi.liveDispatchSSEUrl(customerId);
    const es = new EventSourcePolyfill(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    const flushSseLogs = () => {
      sseLogFlushTimerRef.current = null;
      if (pendingSseLogsRef.current.length === 0) return;
      const batch = pendingSseLogsRef.current;
      pendingSseLogsRef.current = [];
      setSseLogs((prev) => {
        const next = [...batch, ...prev];
        return next.length > MAX_SSE_LOGS ? next.slice(0, MAX_SSE_LOGS) : next;
      });
    };

    const pushSseLog = (entry: Omit<SseLogEntry, 'id' | 'timestamp'>) => {
      if (!canViewSseLogs) return;
      sseLogSeqRef.current += 1;
      pendingSseLogsRef.current.unshift({
        ...entry,
        id: `${Date.now()}-${sseLogSeqRef.current}`,
        timestamp: Date.now(),
      });
      if (pendingSseLogsRef.current.length > MAX_SSE_LOGS) {
        pendingSseLogsRef.current.length = MAX_SSE_LOGS;
      }
      if (sseLogFlushTimerRef.current == null) {
        sseLogFlushTimerRef.current = setTimeout(flushSseLogs, 300);
      }
    };

    es.onmessage = (e: any) => {
      let outer: {
        channel?: string;
        message?: string | Record<string, any>;
        sse_heartbeat?: boolean;
      };
      try {
        outer = JSON.parse(e.data as string);
      } catch (_) {
        pushSseLog({
          channel: '',
          action: null,
          device: null,
          asset: null,
          details: null,
          batteryLevel: null,
          name: null,
          raw: String(e.data),
          isJson: false,
        });
        return;
      }

      if (outer?.sse_heartbeat) return;

      if (typeof outer?.channel !== 'string' || outer?.message === undefined) {
        pushSseLog({
          channel: outer?.channel ?? '',
          action: null,
          device: null,
          asset: null,
          details: null,
          batteryLevel: null,
          name: null,
          raw: JSON.stringify(outer, null, 2),
          isJson: true,
        });
        return;
      }

      let msg: Record<string, any>;
      try {
        msg = (
          typeof outer.message === 'string'
            ? JSON.parse(outer.message)
            : outer.message
        ) as Record<string, any>;
      } catch (_) {
        pushSseLog({
          channel: outer.channel,
          action: null,
          device: null,
          asset: null,
          details: null,
          batteryLevel: null,
          name: null,
          raw:
            typeof outer.message === 'string'
              ? outer.message
              : JSON.stringify(outer.message),
          isJson: false,
        });
        return;
      }

      pushSseLog({
        channel: outer.channel,
        action: (msg?.action as string | undefined) ?? null,
        device: (msg?.device as string | undefined) ?? null,
        asset: (msg?.asset as string | undefined) ?? null,
        details: (msg?.details as string | undefined) ?? null,
        batteryLevel: (msg?.batteryLevel as number | undefined) ?? null,
        name: (msg?.name as string | undefined) ?? null,
        raw: JSON.stringify(msg, null, 2),
        isJson: true,
      });

      try {
        if (outer.channel !== `xtab:${customerCode}:last:pubsub`) return;

        const device = msg?.device as string | undefined;
        if (!device) return;

        if (msg?.action === 'LOGIN') {
          const operatorName = msg?.operatorName as string | undefined;
          if (!operatorName) return;
          setLastStatuses((prev) => ({
            ...prev,
            [device]: {
              payload: prev[device]?.payload ?? null,
              operatorName,
              operatorLoggedOut: false,
            },
          }));
          return;
        }

        if (msg?.action === 'LOGOUT') {
          const operatorName = msg?.operatorName as string | undefined;
          if (!operatorName) return;
          setLastStatuses((prev) => ({
            ...prev,
            [device]: {
              payload: prev[device]?.payload ?? null,
              operatorName,
              operatorLoggedOut: true,
            },
          }));
          return;
        }

        const payloadStatus = msg?.payload?.status as string | undefined;
        const reason = msg?.payload?.reason as string | undefined;
        const description = msg?.payload?.description as string | undefined;
        if (payloadStatus) {
          setLastStatuses((prev) => ({
            ...prev,
            [device]: {
              payload: {
                status: payloadStatus,
                reason: reason ?? '',
                description: description ?? '',
              },
              operatorName: prev[device]?.operatorName ?? null,
              operatorLoggedOut: prev[device]?.operatorLoggedOut,
            },
          }));
        }
      } catch (err) {
        console.error('[assets-status] SSE parse error:', err);
      }
    };

    es.onerror = () => {
      // EventSourcePolyfill auto-reconnects — no action needed
    };

    return () => {
      es.close();
      if (sseLogFlushTimerRef.current != null) {
        clearTimeout(sseLogFlushTimerRef.current);
        sseLogFlushTimerRef.current = null;
      }
    };
  }, [accessToken, customerId, customerCode, dispatchApi, canViewSseLogs]);

  const statusGroups = useMemo(() => {
    const map = new Map<string, { colorCode: string; reasons: string[] }>();
    statusCodes.forEach((sc) => {
      const key = sc.status.trim().toLowerCase();
      if (!map.has(key)) {
        map.set(key, { colorCode: sc.colorCode, reasons: [] });
      }
      const group = map.get(key);
      const cat = sc.reasonCategory?.trim();
      if (group && cat && !group.reasons.includes(cat)) {
        group.reasons.push(cat);
      }
    });
    return map;
  }, [statusCodes]);

  // status+reasonCategory → every configured description for that pair (a
  // reason category can have several status codes, each with its own
  // description, e.g. "Fuel" → "Fuel alpha" / "Traveling To Fuelshop" /
  // "Fuel Ran Out") — feeds the Description select in the status modal.
  const descriptionsByStatusReason = useMemo(() => {
    const map = new Map<string, string[]>();
    statusCodes.forEach((sc) => {
      const statusKey = sc.status?.trim().toLowerCase();
      const reasonKey = sc.reasonCategory?.trim().toLowerCase();
      if (!statusKey || !reasonKey || !sc.description) return;
      const key = `${statusKey}__${reasonKey}`;
      const list = map.get(key);
      if (list) {
        if (!list.includes(sc.description)) list.push(sc.description);
      } else {
        map.set(key, [sc.description]);
      }
    });
    return map;
  }, [statusCodes]);

  const getDescriptionOptions = useCallback(
    (status: string | null | undefined, reason: string | null | undefined) =>
      descriptionsByStatusReason.get(
        `${status?.trim().toLowerCase() ?? ''}__${
          reason?.trim().toLowerCase() ?? ''
        }`
      ) ?? [],
    [descriptionsByStatusReason]
  );

  // Description is the specific, dispatcher-picked note for the asset's
  // current reason category — prefer it, but fall back to the raw reason
  // category for statuses set before this field existed.
  const resolveReasonDisplay = useCallback(
    (
      _status: string | null | undefined,
      reason: string | null | undefined,
      description?: string | null
    ): string | null => {
      const trimmedDescription = description?.trim();
      if (trimmedDescription) return trimmedDescription;
      return reason ?? null;
    },
    []
  );

  const equipmentTypeOptions = useMemo(
    () =>
      Array.from(
        new Set(assets.map((a) => a.assetType?.trim().toLowerCase()))
      ).filter(Boolean),
    [assets]
  );

  const statusOptions = useMemo(
    () =>
      Array.from(
        new Set(
          Object.values(lastStatuses)
            .map((s) => s.payload?.status?.trim().toLowerCase())
            .filter(Boolean)
        )
      ) as string[],
    [lastStatuses]
  );

  const filteredAssets = useMemo(() => {
    const term = search.trim().toLowerCase();
    return assets.filter((asset) => {
      const st = lastStatuses[asset.symbotId];
      const assetTypeKey = asset.assetType?.trim().toLowerCase() ?? '';
      const statusKey = st?.payload?.status?.trim().toLowerCase() ?? '';

      if (
        equipmentTypeFilter.length > 0 &&
        !equipmentTypeFilter.includes(assetTypeKey)
      ) {
        return false;
      }
      if (statusFilter.length > 0 && !statusFilter.includes(statusKey)) {
        return false;
      }
      if (term.length === 0) return true;

      const operatorName = st?.operatorName?.toLowerCase() ?? '';
      return (
        asset.bumperNumber?.toLowerCase().includes(term) ||
        operatorName.includes(term)
      );
    });
  }, [assets, lastStatuses, search, equipmentTypeFilter, statusFilter]);

  const rows = useMemo(
    () =>
      filteredAssets.map((asset, index) => {
        const st = lastStatuses[asset.symbotId];
        const statusRaw = st?.payload?.status ?? null;
        const statusBg = statusRaw
          ? statusGroups.get(statusRaw.toLowerCase())?.colorCode ??
            STATUS_COLORS[statusRaw.toLowerCase()] ??
            theme.palette.action.selected
          : null;
        const rawKey = statusRaw?.toLowerCase() ?? '';
        const displayName =
          rawKey === 'active' ? 'READY' : statusRaw?.toUpperCase() ?? '';
        const reasonText = resolveReasonDisplay(
          statusRaw,
          st?.payload?.reason,
          st?.payload?.description
        );
        const statusLabel = statusRaw
          ? [displayName, reasonText].filter(Boolean).join(' · ')
          : '—';
        const equipmentType = asset.makeModelDetails
          ? `${asset.makeModelDetails.make} ${asset.makeModelDetails.model}`
          : asset.assetType;
        const nearest = asset.nearestLocation;
        const nearestFullLocation = nearest
          ? locationsById.get(nearest.locationId) ?? null
          : null;

        return {
          id: asset.symbotId ?? asset.bumperNumber ?? `asset-${index}`,
          asset,
          bumperNumber: asset.bumperNumber ?? '—',
          equipmentType: equipmentType ?? '—',
          operatorName: st?.operatorName ?? '—',
          operatorLoggedOut: st?.operatorLoggedOut ?? false,
          statusRaw,
          statusLabel,
          statusBg,
          nearestName: nearest?.name ?? '—',
          nearest,
          nearestFullLocation,
        };
      }),
    [
      filteredAssets,
      lastStatuses,
      statusGroups,
      locationsById,
      theme,
      resolveReasonDisplay,
    ]
  );

  type AssetGridRow = (typeof rows)[number];

  const columns: Array<GridColDef<AssetGridRow>> = useMemo(
    () => [
      {
        field: 'bumperNumber',
        headerName: t('assets_status_col_name'),
        minWidth: 160,
        flex: 1,
        sortable: true,
        sortComparator: (v1: string, v2: string) =>
          v1.localeCompare(v2, undefined, {
            numeric: true,
            sensitivity: 'base',
          }),
        renderCell: ({ row }: { row: AssetGridRow }) => (
          <Typography fontWeight={700} fontSize={13}>
            {row.bumperNumber}
          </Typography>
        ),
      },
      {
        field: 'equipmentType',
        headerName: t('assets_status_col_equipment_type'),
        minWidth: 180,
        flex: 1,
        sortable: true,
      },
      {
        field: 'operatorName',
        headerName: t('assets_status_col_operator'),
        minWidth: 180,
        flex: 1,
        sortable: true,
        renderCell: ({ row }: { row: AssetGridRow }) => {
          if (row.operatorName === '—') return row.operatorName;
          if (!row.operatorLoggedOut) return row.operatorName;
          return (
            <Box component="span" sx={{ color: 'error.main' }}>
              {t('assets_status_operator_logged_out', {
                operatorName: row.operatorName,
              })}
            </Box>
          );
        },
      },
      {
        field: 'statusLabel',
        headerName: t('assets_status_col_status_reason'),
        minWidth: 220,
        flex: 1.2,
        sortable: true,
        renderCell: ({ row }: { row: AssetGridRow }) => (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: row.statusRaw ? 1 : 0,
              py: row.statusRaw ? 0.4 : 0,
              borderRadius: 1.5,
              backgroundColor: row.statusBg ?? 'transparent',
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: row.statusRaw ? '#fff' : 'text.secondary',
              }}
            >
              {row.statusLabel}
            </Box>
            <IconButton
              size="small"
              onClick={() => {
                setStatusModal({
                  asset: row.asset,
                  status: row.statusRaw ?? '',
                  reason:
                    lastStatuses[row.asset.symbotId]?.payload?.reason ?? '',
                  description:
                    lastStatuses[row.asset.symbotId]?.payload?.description ??
                    '',
                });
              }}
              sx={{
                p: 0.1,
                color: row.statusRaw
                  ? 'rgba(255,255,255,0.7)'
                  : 'text.secondary',
              }}
            >
              <EditIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Box>
        ),
      },
      {
        field: 'nearestName',
        headerName: t('assets_status_col_last_location'),
        minWidth: 220,
        flex: 1.2,
        sortable: true,
        renderCell: ({ row }: { row: AssetGridRow }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <span>{row.nearestName}</span>
            {row.nearest && (
              <Tooltip title={t('assets_status_view_on_map')}>
                <IconButton
                  size="small"
                  onClick={() => {
                    const nearest = row.nearest;
                    if (!nearest) return;
                    // storeAssets (Redux) is the same source Fleet
                    // Overview reads make/model from — asset.
                    // makeModelDetails is a separate, less
                    // reliable per-row lookup that can be empty
                    // even when the canonical asset has it set.
                    const storeAsset = storeAssets.find(
                      (a) => a.device?.deviceId === row.asset.symbotId
                    );
                    setMapLocation({
                      name: row.nearestFullLocation?.name ?? nearest.name,
                      locationType: row.nearestFullLocation?.locationType,
                      centroidLat: row.nearestFullLocation?.centroidLat ?? null,
                      centroidLon: row.nearestFullLocation?.centroidLon ?? null,
                      zoneGeog: row.nearestFullLocation?.zoneGeog ?? null,
                      asset: {
                        bumperNumber: row.asset.bumperNumber,
                        assetType: row.asset.assetType,
                        make:
                          storeAsset?.make ?? row.asset.makeModelDetails?.make,
                        model:
                          storeAsset?.model ??
                          row.asset.makeModelDetails?.model,
                        lat: row.asset.lat,
                        lon: row.asset.lon,
                      },
                    });
                  }}
                  sx={{ p: 0.1, color: 'text.secondary' }}
                >
                  <FmdGoodOutlinedIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ],
    [t, lastStatuses, storeAssets]
  );

  const saveStatus = async () => {
    if (!statusModal) return;
    const { asset, status, reason, description } = statusModal;
    const assetId = getAssetId(asset.symbotId);
    if (!assetId) return;
    setStatusSaving(true);
    try {
      await dispatchApi.updateAssetStatus(
        assetId,
        status,
        reason?.trim() || undefined,
        description?.trim() || undefined
      );
      setLastStatuses((prev) => ({
        ...prev,
        [asset.symbotId]: {
          payload: {
            status,
            reason: reason?.trim() ?? '',
            description: description?.trim() ?? '',
          },
          operatorName: prev[asset.symbotId]?.operatorName ?? null,
        },
      }));
      setStatusModal(null);
    } catch (_) {
      // keep modal open so user can retry
    } finally {
      setStatusSaving(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setEquipmentTypeFilter([]);
    setStatusFilter([]);
  };

  const hasActiveFilters =
    search.length > 0 ||
    equipmentTypeFilter.length > 0 ||
    statusFilter.length > 0;

  const textPrimary = theme.palette.text.primary;

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 115px)',
        }}
      >
        <CircularProgress size={36} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 115px)',
        p: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          {t('assets_status_title')}
        </Typography>
        {canViewSseLogs && (
          <SseLogsViewer
            logs={sseLogs}
            onClear={() => {
              pendingSseLogsRef.current = [];
              setSseLogs([]);
            }}
            deviceLabels={deviceLabelsBySymbotId}
          />
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          mb: 2,
        }}
      >
        <TextField
          size="small"
          placeholder={t('assets_status_search_placeholder') ?? ''}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
          sx={{ minWidth: 220 }}
        />

        <Select
          size="small"
          multiple
          displayEmpty
          value={equipmentTypeFilter}
          onChange={(e: SelectChangeEvent<string[]>) => {
            const value = e.target.value;
            setEquipmentTypeFilter(
              typeof value === 'string' ? value.split(',') : value
            );
          }}
          renderValue={(selected) =>
            selected.length === 0
              ? t('assets_status_filter_equipment_type')
              : selected.join(', ')
          }
          sx={{ minWidth: 200 }}
        >
          {equipmentTypeOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>

        <Select
          size="small"
          multiple
          displayEmpty
          value={statusFilter}
          onChange={(e: SelectChangeEvent<string[]>) => {
            const value = e.target.value;
            setStatusFilter(
              typeof value === 'string' ? value.split(',') : value
            );
          }}
          renderValue={(selected) =>
            selected.length === 0
              ? t('assets_status_filter_status')
              : selected.join(', ')
          }
          sx={{ minWidth: 180 }}
        >
          {statusOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>

        {hasActiveFilters && (
          <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters}>
            {t('assets_status_clear_filters')}
          </Button>
        )}

        <Tooltip title={t('assets_status_refresh')}>
          <span>
            <IconButton
              size="small"
              disabled={refreshing}
              onClick={handleRefresh}
              sx={{ ml: 'auto' }}
            >
              {refreshing ? (
                <CircularProgress size={16} />
              ) : (
                <RefreshIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ width: '100%' }}>
        <CustomStyledDataGrid
          rows={rows}
          columns={columns}
          disableRowSelectionOnClick
          autoHeight
          getRowHeight={() => 52}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          localeText={{ noRowsLabel: t('assets_status_no_assets') }}
          sx={{ border: 'none' }}
        />
      </Box>

      <LocationZoneMapModal
        open={Boolean(mapLocation)}
        onClose={() => {
          setMapLocation(null);
        }}
        location={mapLocation}
      />

      <Dialog
        open={Boolean(statusModal)}
        onClose={() => {
          setStatusModal(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t('assets_status_edit_status_title')}
          {statusModal ? ` — ${statusModal.asset.bumperNumber}` : ''}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {Array.from(statusGroups.entries()).map(([key, group]) => {
              const isSelected = statusModal?.status?.toLowerCase() === key;
              return (
                <Chip
                  key={key}
                  label={key.toUpperCase()}
                  onClick={() => {
                    if (statusModal) {
                      const defaultReason = group.reasons[0] ?? '';
                      setStatusModal({
                        ...statusModal,
                        status: key,
                        reason: defaultReason,
                        description:
                          getDescriptionOptions(key, defaultReason)[0] ?? '',
                      });
                    }
                  }}
                  sx={{
                    backgroundColor: isSelected
                      ? group.colorCode
                      : theme.palette.action.selected,
                    color: isSelected ? '#fff' : textPrimary,
                    fontWeight: 700,
                  }}
                />
              );
            })}
          </Box>

          {statusModal?.status &&
            (statusGroups.get(statusModal.status.toLowerCase())?.reasons
              .length ?? 0) > 0 && (
              <Select
                fullWidth
                size="small"
                value={statusModal.reason}
                onChange={(e) => {
                  if (statusModal) {
                    const nextReason = e.target.value;
                    setStatusModal({
                      ...statusModal,
                      reason: nextReason,
                      description:
                        getDescriptionOptions(
                          statusModal.status,
                          nextReason
                        )[0] ?? '',
                    });
                  }
                }}
                sx={{ mt: 2 }}
              >
                {statusGroups
                  .get(statusModal.status.toLowerCase())
                  ?.reasons.map((reason) => (
                    <MenuItem key={reason} value={reason}>
                      {reason}
                    </MenuItem>
                  ))}
              </Select>
            )}

          {statusModal?.status && statusModal.reason && (
            <Autocomplete
              freeSolo
              size="small"
              sx={{ mt: 2 }}
              options={getDescriptionOptions(
                statusModal.status,
                statusModal.reason
              )}
              value={statusModal.description}
              onChange={(_e, value) => {
                if (statusModal) {
                  setStatusModal({ ...statusModal, description: value ?? '' });
                }
              }}
              onInputChange={(_e, value) => {
                if (statusModal) {
                  setStatusModal({ ...statusModal, description: value });
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('hm_edit_description_label')}
                  placeholder={t('hm_description_placeholder')}
                />
              )}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setStatusModal(null);
            }}
          >
            {t('assets_status_cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={statusSaving || !statusModal?.status}
            onClick={() => {
              void saveStatus();
            }}
          >
            {t('assets_status_save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const AssetsStatusView: React.FC = () => (
  <CustomMuiThemeProvider>
    <AssetsStatusInner />
  </CustomMuiThemeProvider>
);

export default AssetsStatusView;
